import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import MenuItem from "./MenuItem";
import { getNavServiceCategories } from "../../../data/navServiceCategories";

const DESKTOP_NAV_MIN = 961;

const ServicesMenu = memo(({ onNavigate, mobileDrawerOpen }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const categories = useMemo(() => getNavServiceCategories(), []);
  const [activeIndex, setActiveIndex] = useState(0);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [megaHover, setMegaHover] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const [suppressDropdown, setSuppressDropdown] = useState(false);
  const locationSkipRef = useRef(true);

  const panelOpen =
    !suppressDropdown && (mobileExpanded || megaHover || focusWithin);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const id = requestAnimationFrame(() => {
      window.dispatchEvent(
        new CustomEvent("vh-services-mega-open", {
          detail: { open: panelOpen },
        })
      );
    });
    return () => {
      cancelAnimationFrame(id);
    };
  }, [panelOpen]);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("vh-services-mega-open", { detail: { open: false } })
        );
      }
    };
  }, []);

  const active = categories[Math.min(activeIndex, categories.length - 1)];

  useEffect(() => {
    if (activeIndex >= categories.length) setActiveIndex(0);
  }, [activeIndex, categories.length]);

  useEffect(() => {
    if (mobileDrawerOpen === false) {
      setMobileExpanded(false);
    }
  }, [mobileDrawerOpen]);

  useEffect(() => {
    if (locationSkipRef.current) {
      locationSkipRef.current = false;
      return;
    }
    setMobileExpanded(false);
    setMegaHover(false);
    setFocusWithin(false);
    setSuppressDropdown(true);
  }, [location.pathname, location.search, location.hash]);

  const handleNavigateAway = useCallback(() => {
    setMobileExpanded(false);
    setMegaHover(false);
    setFocusWithin(false);
    setSuppressDropdown(true);
    onNavigate?.();
  }, [onNavigate]);

  const toggleMobilePanel = useCallback(() => {
    if (typeof window !== "undefined" && window.innerWidth >= DESKTOP_NAV_MIN) {
      return;
    }
    setMobileExpanded((v) => !v);
  }, []);

  const selectCategory = useCallback((index) => {
    setActiveIndex(index);
  }, []);

  if (categories.length === 0) return null;

  return (
    <li
      className={`services menu-item menu-item-has-children vh-nav-services${suppressDropdown ? " vh-nav-services--suppress" : ""}${panelOpen ? " vh-nav-services--mega-open" : ""}`}
      onMouseEnter={() => {
        setMegaHover(true);
        setSuppressDropdown(false);
      }}
      onMouseLeave={(e) => {
        /* Clearing suppress here can reopen the mega (leave fires when panel hides). */
        setMegaHover(false);
        if (
          typeof window !== "undefined" &&
          window.innerWidth >= DESKTOP_NAV_MIN
        ) {
          setActiveIndex(0);
          const btn = e.currentTarget.querySelector(".vh-nav-megatoggle");
          if (btn && document.activeElement === btn) {
            btn.blur();
          }
        }
      }}
      onFocus={() => {
        setFocusWithin(true);
        setSuppressDropdown(false);
      }}
      onBlur={(e) => {
        const { relatedTarget } = e;
        if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
          setFocusWithin(false);
        }
      }}
    >
      <button
        type="button"
        className={`nav-link vh-nav-megatoggle${mobileExpanded ? " active" : ""}`}
        aria-expanded={panelOpen}
        aria-haspopup="true"
        aria-controls="vh-nav-services-panel"
        id="vh-nav-services-trigger"
        onClick={toggleMobilePanel}
      >
        <span className="menu-title">{t("nav.services")}</span>
        <span className="dropdown-menu-opener" aria-hidden="true" />
      </button>
      <div
        className={`dropdown-menu__wrapper vh-nav-services-panel ${mobileExpanded ? "is-open" : ""}`}
        id="vh-nav-services-panel"
        role="region"
        aria-labelledby="vh-nav-services-trigger"
      >
        <div className="vh-nav-services-panel__inner">
          <p className="vh-nav-services-panel__eyebrow">
            {t("nav.servicesEyebrow")}
          </p>
          <div className="vh-nav-services-layout">
            <div
              className="vh-nav-services-rail"
              role="tablist"
              aria-label={t("nav.servicesEyebrow")}
            >
              {categories.map((cat, index) => {
                const selected = index === activeIndex;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    role="tab"
                    id={`vh-nav-tab-${cat.id}`}
                    aria-selected={selected}
                    aria-controls="vh-nav-services-tabpanel"
                    tabIndex={0}
                    className={`vh-nav-rail-btn${selected ? " vh-nav-rail-btn--active" : ""}`}
                    data-vh-cat={cat.id}
                    onMouseEnter={() => {
                      if (
                        typeof window !== "undefined" &&
                        window.innerWidth >= DESKTOP_NAV_MIN
                      ) {
                        setActiveIndex(index);
                      }
                    }}
                    onFocus={() => setActiveIndex(index)}
                    onClick={() => selectCategory(index)}
                  >
                    <span className="vh-nav-rail-btn__accent" aria-hidden="true" />
                    <span className="vh-nav-rail-btn__title">
                      {t(`nav.categories.${cat.id}`)}
                    </span>
                    <span className="vh-nav-rail-btn__hint">
                      {t(`nav.categoryHints.${cat.id}`)}
                    </span>
                  </button>
                );
              })}
            </div>
            <div
              className="vh-nav-services-detail"
              id="vh-nav-services-tabpanel"
              role="tabpanel"
              aria-labelledby={
                active ? `vh-nav-tab-${active.id}` : undefined
              }
            >
              {active ? (
                <ul className="dropdown-menu vh-nav-service-list">
                  {active.items.map((service) => (
                    <MenuItem
                      key={service.id}
                      service={service}
                      categorySlug={active.id}
                      onNavigate={handleNavigateAway}
                    />
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </li>
  );
});

export default ServicesMenu;
